import React, { memo } from "react";
import DropdownButton from "../../../components/DropdownButtonMC";
import PaymentTransactionModalMC from "./PaymentTransactionModalMC";
import LoadingMC from "../../../components/LoadingMC";
import useCustomerOrderPaymentStatusActions from "../../../hooks/inbox/actions/useCustomerOrderPaymentStatusActions";
import useCustomerOrderPaymentStatusUI from "../../../hooks/inbox/ui/useCustomerOrderPaymentStatusUI";
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';

/**
 * Componente Molecule optimizado para selección y cambio de estado de pago de una orden de cliente.
 *
 * Separa lógica de negocio (Actions Layer) y lógica UI (UI Layer) según estándares Mercately.
 *
 * @component CustomerOrderPaymentStatusSelectorMC
 * @category Component - Molecule
 * @param {Object} props
 * @param {Object} props.order - Objeto de la orden
 * @param {Function} props.updateOrderInList - Callback para actualizar la orden en la lista
 * @returns {JSX.Element} Selector de estado de pago renderizado
 * @see useCustomerOrderPaymentStatusActions
 * @see useCustomerOrderPaymentStatusUI
 * @since 1.0.0
 * @version 2.0.0 - Refactor separación de capas y optimización de performance
 */
const CustomerOrderPaymentStatusSelectorMC = memo(({ order, updateOrderInList }) => {
  // Actions Layer: lógica de negocio
  const {
    loading,
    openModal,
    newStatus,
    handleStatusChange,
    setOpenModal,
  } = useCustomerOrderPaymentStatusActions(order, updateOrderInList);

  // UI Layer: lógica de presentación y opciones
  const {
    paymentStatusLabel,
    paymentStatusOptions,
    containerClassNamePaymentStatus,
  } = useCustomerOrderPaymentStatusUI(order, handleStatusChange);

  // Debug tracking de dependencias críticas
  useTraceUpdate('CustomerOrderPaymentStatusSelectorMC', {
    orderId: order?.id,
    paymentStatus: order?.payment_status,
    paymentTransaction: order?.payment_transaction?.id,
    loading
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  if (loading) return <LoadingMC loading={loading} spinnerClassName="!tw-w-4 !tw-h-4" />;

  return (
    <>
      <DropdownButton
        variant="TEXT_PLAIN"
        text={paymentStatusLabel}
        items={paymentStatusOptions}
        containerClassName={containerClassNamePaymentStatus}
        className={`!tw-h-[20px] !tw-min-h-0 !tw-py-1 !tw-px-1 ${containerClassNamePaymentStatus}`}
      />
      {openModal && (
        <PaymentTransactionModalMC
          order={order}
          newStatus={newStatus}
          toggleModal={() => setOpenModal(false)}
          updateOrderInList={updateOrderInList}
        />
      )}
    </>
  );
});

CustomerOrderPaymentStatusSelectorMC.displayName = 'CustomerOrderPaymentStatusSelectorMC';

export default CustomerOrderPaymentStatusSelectorMC;